/**
 * ReceptionQueue — the intelligent waiting-room view.
 *
 * Doctor continuity is preserved by default. Cross-doctor transfer only
 * appears as a recommendation (and only when clinic settings enable it),
 * never as a default action.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Users, Clock, ChevronRight, Loader2, RefreshCcw, CheckCircle2, LogOut,
  Stethoscope, Armchair, Sparkles, AlertTriangle, Check, X as XIcon,
  BarChart3, CalendarClock, TrendingUp, AlertCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { useClinic } from "@/lib/useClinic";
import { modeFromClinic } from "@/lib/workflowMode";
import { Link } from "react-router-dom";

const STATUS_TONE = {
  waiting:    "bg-[#FDF3ED] text-[#8A3F1E]",
  checked_in: "bg-[#FDF3ED] text-[#8A3F1E]",
  ready:      "bg-[#E8F0EB] text-[#2D6A4F]",
  in_chair:   "bg-[#E8EEFF] text-[#334488]",
};

export default function ReceptionQueue() {
  const [queue, setQueue] = useState([]);
  const [settings, setSettings] = useState({ allow_doctor_reassignment: false, enable_cross_doctor: false });
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const { clinic } = useClinic();
  const mode = modeFromClinic(clinic);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/checkin/queue");
      setQueue(data.queue || []);
      setSettings(data.settings || {});
      if (!selectedId && data.queue?.length) setSelectedId(data.queue[0].id);
    } catch {
      toast.error("Could not load queue");
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  // Poll every 15 s — the queue is the only thing on this page.
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const selected = queue.find((q) => q.id === selectedId) || null;

  return (
    <div className="min-h-screen bg-linen" data-testid="reception-queue-page">
      <header className="border-b border-[#E8EAE9] bg-white px-6 py-4">
        <div className="flex items-center justify-between max-w-[1400px] mx-auto">
          <div>
            <div className="overline text-ink-soft">Reception</div>
            <div className="font-display text-2xl mt-1">Waiting Room</div>
          </div>
          <div className="flex items-center gap-3 text-sm text-ink-mid">
            <span className="inline-flex items-center gap-1"><Users className="w-4 h-4" /> {queue.length} in queue</span>
            <button data-testid="refresh-queue" onClick={load} className="p-1.5 rounded-lg hover:bg-linen">
              <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6 p-6">
        <div className="space-y-6">
          {mode === "solo" && (
            <div
              className="rounded-2xl border border-[#EAC5AE] bg-[#FDF3ED] px-5 py-4 text-sm text-[#8A3F1E] flex items-center justify-between gap-4"
              data-testid="solo-mode-banner"
            >
              <div>
                <b>Solo mode is active.</b> The Waiting Room is optional — you can start visits directly from the Calendar with a single click.
              </div>
              <Link
                to="/app/calendar"
                className="inline-flex items-center gap-2 rounded-full bg-pine text-white px-4 py-2 text-xs font-medium hover:bg-pine/90"
                data-testid="solo-mode-go-calendar"
              >
                Open Calendar
              </Link>
            </div>
          )}
          <AnalyticsStrip />
          <DayProjectionPanel />
          <QueueList queue={queue} loading={loading} selectedId={selectedId} onSelect={setSelectedId} />
        </div>
        <div className="space-y-4">
          {selected
            ? <RecommendationsPanel entry={selected} settings={settings} onDone={load} />
            : <EmptyState />}
          <OptimizationBanner settings={settings} />
        </div>
      </main>
    </div>
  );
}


/* -------------------------------------------------- Queue list ---- */
function QueueList({ queue, loading, selectedId, onSelect }) {
  if (loading && queue.length === 0) {
    return <div className="card-widget py-12 text-center text-ink-soft"><Loader2 className="w-4 h-4 animate-spin inline" /></div>;
  }
  if (queue.length === 0) {
    return (
      <div className="card-widget py-16 text-center">
        <Users className="w-10 h-10 mx-auto text-ink-soft mb-3" />
        <div className="font-display text-lg text-ink">No patients in the queue right now</div>
        <div className="text-sm text-ink-soft mt-1">Check patients in from the Calendar to start.</div>
      </div>
    );
  }
  return (
    <ul className="space-y-2">
      {queue.map((q) => (
        <li key={q.id}>
          <button
            data-testid={`queue-item-${q.id}`}
            onClick={() => onSelect(q.id)}
            className={`w-full text-left rounded-2xl border p-4 transition ${
              selectedId === q.id
                ? "bg-white border-pine shadow-sm"
                : "bg-white/70 border-[#E8EAE9] hover:bg-white hover:border-pine/30"
            }`}>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-linen text-ink font-medium grid place-items-center shrink-0">
                {(q.patient_name || "?").slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-ink truncate">{q.patient_name || "Unknown"}</span>
                  {q.clinic_patient_id && (
                    <span className="text-[10px] font-mono text-ink-soft" data-testid={`queue-cpid-${q.id}`}>
                      {q.clinic_patient_id}
                    </span>
                  )}
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${STATUS_TONE[q.current_status] || "bg-[#EDEDED] text-[#6B7280]"}`}>
                    {q.current_status.replace("_", " ")}
                  </span>
                </div>
                <div className="text-xs text-ink-mid mt-1 flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1"><Stethoscope className="w-3 h-3" /> {q.doctor_name || "Unassigned"}</span>
                  {q.chair_name && (
                    <span className="inline-flex items-center gap-1"><Armchair className="w-3 h-3" /> {q.chair_name}</span>
                  )}
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> ~{q.eta_minutes} min wait</span>
                </div>
                {q.procedure && <div className="text-xs text-ink-soft mt-1 truncate">{q.procedure}</div>}
              </div>
              <ChevronRight className="w-4 h-4 text-ink-soft mt-2" />
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}


/* -------------------------------------------------- Recommendations ---- */
function RecommendationsPanel({ entry, settings, onDone }) {
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKind, setBusyKind] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/checkin/${entry.id}/recommendations`);
      setRecs(data.recommendations || []);
    } catch {
      setRecs([]);
    } finally { setLoading(false); }
  }, [entry.id]);

  useEffect(() => { load(); }, [load]);

  const apply = async (rec) => {
    if (rec.kind === "cross_doctor_swap") {
      if (!window.confirm(
        `This transfers the patient to ${rec.action.doctor_name}, changing the treating doctor. Continue?`,
      )) return;
    }
    setBusyKind(rec.kind + (rec.action?.task_id || ""));
    try {
      const a = rec.action;
      if (a.type === "assign_chair") {
        await api.patch(`/checkin/${entry.id}/chair`, { chair_id: a.chair_id });
      } else if (a.type === "start_pre_task") {
        await api.post(`/checkin/${entry.id}/pre-tasks`, { task_id: a.task_id });
      } else if (a.type === "notify_delay") {
        toast.info(`Please notify ${entry.patient_name || "the next patient"} of the delay via WhatsApp / phone.`);
      } else if (a.type === "reorder_queue") {
        toast.info("Seat the shorter-procedure patient first via their queue card.");
      } else if (a.type === "transfer_doctor") {
        await api.post(`/checkin/${entry.id}/transfer-doctor`, {
          to_doctor_id: a.doctor_id, approved: true,
          reason: "Approved from reception recommendations",
        });
      }
      toast.success("Applied");
      await load();
      onDone?.();
    } catch (e) {
      toast.error(apiError(e, "Could not apply recommendation"));
    } finally { setBusyKind(null); }
  };

  return (
    <div className="card-widget" data-testid="recommendations-panel">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-pine" />
        <div className="font-display text-lg">AI recommendations</div>
      </div>
      <div className="text-xs text-ink-soft mb-4">
        For <strong>{entry.patient_name}</strong> · doctor: <strong>{entry.doctor_name || "unassigned"}</strong> ·
        ETA <strong>~{entry.eta_minutes} min</strong>. Doctor continuity is preserved unless you explicitly transfer.
      </div>

      {loading ? (
        <div className="py-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline text-ink-soft" /></div>
      ) : recs.length === 0 ? (
        <div className="py-6 text-center text-sm text-ink-soft">
          <CheckCircle2 className="w-6 h-6 mx-auto text-[#2D6A4F] mb-2" />
          Schedule looks optimal — nothing to change.
        </div>
      ) : (
        <ul className="space-y-2">
          {recs.map((r, i) => (
            <li
              key={`${r.kind}-${i}`}
              data-testid={`rec-${r.kind}`}
              className={`rounded-xl border p-3 ${
                r.requires_approval ? "border-[#F5C7A2] bg-[#FDF3ED]" : "border-[#E8EAE9] bg-white"
              }`}>
              <div className="flex items-start gap-2">
                {r.requires_approval && <AlertTriangle className="w-4 h-4 text-[#8A3F1E] mt-0.5 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-ink">{r.title}</div>
                  <div className="text-xs text-ink-mid mt-0.5">{r.detail}</div>
                  {r.est_wait_reduction_min > 0 && (
                    <div className="text-[10px] uppercase tracking-widest text-pine mt-1">
                      Saves ~{r.est_wait_reduction_min} min
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  data-testid={`rec-apply-${r.kind}`}
                  onClick={() => apply(r)}
                  disabled={busyKind === r.kind + (r.action?.task_id || "")}
                  className="text-xs inline-flex items-center gap-1 bg-pine text-linen px-3 py-1.5 rounded-full hover:bg-pine/90 disabled:opacity-50">
                  {busyKind === r.kind + (r.action?.task_id || "") ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  {r.requires_approval ? "Approve & apply" : "Apply"}
                </button>
                <button
                  onClick={load}
                  className="text-xs text-ink-soft hover:text-ink px-3 py-1.5 rounded-full border border-[#E8EAE9]">
                  <XIcon className="w-3 h-3 inline mr-1" /> Dismiss
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 pt-3 border-t border-[#E8EAE9]">
        <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-2">Manual actions</div>
        <div className="flex flex-wrap gap-2">
          <QuickBtn label="Mark in chair" onClick={async () => {
            await api.patch(`/checkin/${entry.id}/status`, { status: "in_chair" });
            toast.success("Marked in chair"); onDone?.();
          }} testid="status-in-chair" />
          <QuickBtn label="Complete visit" icon={CheckCircle2} onClick={async () => {
            await api.patch(`/checkin/${entry.id}/status`, { status: "done" });
            toast.success("Visit completed"); onDone?.();
          }} testid="status-done" />
          <QuickBtn label="Patient left" icon={LogOut} onClick={async () => {
            if (!window.confirm("Mark patient as left / no-show?")) return;
            await api.patch(`/checkin/${entry.id}/status`, { status: "left" });
            toast.success("Marked as left"); onDone?.();
          }} testid="status-left" />
        </div>
      </div>
    </div>
  );
}

function QuickBtn({ label, icon: Icon, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
            className="text-xs inline-flex items-center gap-1 border border-[#E8EAE9] px-3 py-1.5 rounded-full hover:bg-linen text-ink">
      {Icon && <Icon className="w-3 h-3" />} {label}
    </button>
  );
}


function EmptyState() {
  return (
    <div className="card-widget py-10 text-center text-sm text-ink-soft">
      Select a patient from the queue to see AI recommendations.
    </div>
  );
}


function OptimizationBanner({ settings }) {
  if (settings.allow_doctor_reassignment && settings.enable_cross_doctor) {
    return (
      <div className="rounded-xl bg-[#FDF3ED] border border-[#F5C7A2] text-[#8A3F1E] p-3 text-xs">
        <div className="font-medium mb-0.5">Cross-doctor optimisation is ON</div>
        The engine may suggest transferring patients to a different doctor. Every transfer needs your approval.
      </div>
    );
  }
  return (
    <div className="rounded-xl bg-white border border-[#E8EAE9] p-3 text-xs text-ink-mid">
      <div className="font-medium text-ink mb-0.5">Doctor continuity mode</div>
      Every patient stays with their assigned doctor. Enable Cross-Doctor in Settings → Scheduling to allow transfer suggestions.
    </div>
  );
}


/* -------------------------------------------------- Analytics strip ---- */
function AnalyticsStrip() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);

  useEffect(() => {
    let alive = true;
    api.get(`/checkin/analytics?days=${days}`)
       .then((r) => { if (alive) setData(r.data); })
       .catch(() => setData(null));
    return () => { alive = false; };
  }, [days]);

  if (!data) return null;
  const topDr = data.doctor_utilisation.find((d) => d.utilisation_pct > 0);
  const topCh = data.chair_utilisation.find((c) => c.utilisation_pct > 0);
  return (
    <section className="card-widget" data-testid="analytics-strip">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-pine" />
          <div className="font-display text-lg">Waiting-room analytics</div>
        </div>
        <select
          data-testid="analytics-window"
          value={days} onChange={(e) => setDays(Number(e.target.value))}
          className="text-xs border border-[#E8EAE9] rounded-full px-3 py-1 bg-white">
          {[1, 7, 30, 90].map((d) => <option key={d} value={d}>Last {d}d</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Avg wait" value={`${data.avg_wait_minutes} min`} testid="stat-wait" />
        <Stat label="Patients served" value={data.patients_served} testid="stat-served" />
        <Stat label="Top doctor" value={topDr ? `${topDr.name.split(" ")[0]} · ${topDr.utilisation_pct}%` : "—"}
              testid="stat-top-doctor" />
        <Stat label="Top chair" value={topCh ? `${topCh.name} · ${topCh.utilisation_pct}%` : "—"}
              testid="stat-top-chair" />
      </div>
      <details className="mt-3 group">
        <summary className="text-xs text-ink-soft cursor-pointer hover:text-ink inline-flex items-center gap-1">
          <TrendingUp className="w-3 h-3" /> Show all utilisation
        </summary>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          <UtilBars title="Doctors" rows={data.doctor_utilisation} field="doctor_id" />
          <UtilBars title="Chairs"  rows={data.chair_utilisation}  field="chair_id"  />
        </div>
      </details>
    </section>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl bg-linen border border-[#E8EAE9] p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</div>
      <div className="font-display text-xl mt-1">{value}</div>
    </div>
  );
}

function UtilBars({ title, rows, field }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-2">{title}</div>
      <div className="space-y-1.5">
        {rows.slice(0, 8).map((r) => (
          <div key={r[field]} className="flex items-center gap-2">
            <div className="w-24 truncate text-xs text-ink">{r.name}</div>
            <div className="flex-1 h-2 bg-linen rounded-full overflow-hidden">
              <div className="h-full bg-pine" style={{ width: `${r.utilisation_pct}%` }} />
            </div>
            <div className="w-10 text-right text-xs text-ink-mid tabular-nums">{r.utilisation_pct}%</div>
          </div>
        ))}
        {rows.length === 0 && <div className="text-xs text-ink-soft italic">No data</div>}
      </div>
    </div>
  );
}


/* -------------------------------------------------- Day projection ---- */
function DayProjectionPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/checkin/day-projection");
      setData(data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return null;
  if (!data || data.projection.length === 0) return null;

  const summary = data.summary || {};
  const hasDelays = (summary.delayed_count || 0) > 0;
  return (
    <section className="card-widget" data-testid="day-projection-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-pine" />
          <div className="font-display text-lg">Today at a glance</div>
        </div>
        <div className={`text-xs ${hasDelays ? "text-[#8A3F1E]" : "text-ink-mid"}`}>
          {hasDelays
            ? <span><AlertCircle className="w-3 h-3 inline mr-1" />{summary.delayed_count} of {summary.total_today} running late · max delay {summary.max_delay_min} min</span>
            : <span>All {summary.total_today} appointments on track</span>}
        </div>
      </div>
      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-ink-soft border-b border-[#E8EAE9]">
              <th className="py-2 pr-3">Patient</th>
              <th className="py-2 pr-3">Scheduled</th>
              <th className="py-2 pr-3">Projected</th>
              <th className="py-2 pr-3 text-right">Delay</th>
              <th className="py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.projection.slice(0, 12).map((p) => {
              const late = p.delay_minutes > 5;
              const fmt = (iso) => new Date(iso).toLocaleTimeString("en-IN",
                { hour: "2-digit", minute: "2-digit", hour12: false });
              return (
                <tr key={p.appointment_id} className="border-b border-[#F1EEE7] last:border-b-0" data-testid={`proj-${p.appointment_id}`}>
                  <td className="py-2 pr-3 text-ink truncate">{p.patient_name || "Patient"}</td>
                  <td className="py-2 pr-3 text-ink-mid tabular-nums">{fmt(p.scheduled_start)}</td>
                  <td className={`py-2 pr-3 tabular-nums ${late ? "text-[#8A3F1E] font-medium" : "text-ink-mid"}`}>
                    {fmt(p.projected_start)}
                  </td>
                  <td className={`py-2 pr-3 text-right tabular-nums ${late ? "text-[#8A3F1E]" : "text-ink-soft"}`}>
                    {p.delay_minutes > 0 ? `+${p.delay_minutes}m` : "—"}
                  </td>
                  <td className="py-2 text-[11px] uppercase tracking-widest text-ink-soft">{p.status.replace("_", " ")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
